import type { AxiosInstance } from "axios";
import axios from "axios";
import API from "@/utils/api";
import store from "@/store/store";
import { authActions } from "@/slices/auth-slice";
import type {
  ForgotPasswordRequestDto,
  ForgotPasswordResponseDto,
  MeApiResponseDto,
  RefreshTokenResponseDto,
  ResetPasswordRequestDto,
  RegisterRequestDto,
} from "@/domain/auth/auth.api.types";
import type { LoginRequest } from "@/domain/auth/login-request";
import { toLoginWireDto } from "@/utils/login-request";
import type { AuthenticatedUser, AppUser } from "@/domain/auth/authenticated-user";
import {
  assertMeSuccess,
  identityEquals,
  mapAuthenticatedUserDto,
  parseLoginResponse,
  toAppUser,
  type LoginSession,
} from "@/domain/auth/auth.mapper";
import {
  AuthCredentialsError,
  AuthNetworkError,
  AuthProfileError,
  AuthSessionError,
  TokenVersionMismatchError,
  extractApiErrorMessageFromData,
} from "@/domain/auth/auth.errors";
import { tokenStorage, type TokenPair, type TokenStorage } from "@/domain/auth/token-storage";
import { clearAuthDependentClientState } from "@/utils/auth-session-cleanup";
import { revokeOrderNotificationsForSession } from "@/services/order-notifications";
import { invalidateQueryPrefix } from "@/lib/safe-query/run-safe-query";
import { QUERY_KEY_PREFIX } from "@/query-keys/constants";
import {
  extractOAuthAuthUrl,
  oauthCallbackApiPath,
  oauthInitApiPath,
  oauthProviderDisplayName,
  type OAuthProvider,
} from "@/utils/storefront-oauth";

const ME_ENDPOINT = "auth/v1/me";
const LOGIN_ENDPOINT = "auth/v1/token";
const GOOGLE_CALLBACK_ENDPOINT = oauthCallbackApiPath("google");
const REFRESH_ENDPOINT = "auth/v1/refresh";
const FORGOT_PASSWORD_ENDPOINT = "auth/v1/password/forgot";
const RESET_PASSWORD_ENDPOINT = "auth/v1/password/reset";
const REGISTER_ENDPOINT = "users/v1/register-user";
const LOGOUT_ENDPOINT = "auth/v1/logout";

function isNetworkError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  return !error.response || error.code === "ERR_NETWORK";
}

function isUnauthorized(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

function isFailureEnvelope(data: unknown): boolean {
  return Boolean(data) && typeof data === "object" && (data as { success?: unknown }).success === false;
}

function assertAuthActionSucceeded(data: unknown, fallbackMessage: string): void {
  if (!isFailureEnvelope(data)) return;
  throw new AuthSessionError(extractApiErrorMessageFromData(data) ?? fallbackMessage);
}

export class AuthService {
  private readonly api: AxiosInstance;
  private readonly storage: TokenStorage;

  private inflightBootstrap: Promise<void> | null = null;
  private inflightProfile: Promise<AuthenticatedUser> | null = null;
  private inflightRefresh: Promise<TokenPair> | null = null;

  private lastKnownTokenVersion: number | null = null;

  constructor(api: AxiosInstance = API, storage: TokenStorage = tokenStorage) {
    this.api = api;
    this.storage = storage;
  }

  /** App startup: validate session via /me. Deduped. */
  async bootstrap(): Promise<void> {
    if (this.inflightBootstrap) return this.inflightBootstrap;

    this.inflightBootstrap = this.runBootstrap().finally(() => {
      this.inflightBootstrap = null;
    });

    return this.inflightBootstrap;
  }

  private async runBootstrap(): Promise<void> {
    if (!this.storage.hasAccessToken()) {
      store.dispatch(authActions.setUnauthenticated());
      return;
    }

    store.dispatch(authActions.bootstrapStarted());

    try {
      await this.loadProfile({ preserveEnrichment: true });
    } catch (error) {
      if (error instanceof TokenVersionMismatchError || error instanceof AuthSessionError) {
        await this.clearSession();
        store.dispatch(authActions.setUnauthenticated());
        return;
      }

      if (isUnauthorized(error)) {
        try {
          await this.refreshTokens();
          await this.loadProfile({ preserveEnrichment: true });
          return;
        } catch {
          await this.clearSession();
          store.dispatch(authActions.setUnauthenticated());
          return;
        }
      }

      if (isNetworkError(error)) {
        store.dispatch(
          authActions.profileLoadFailed({
            message: new AuthNetworkError().message,
          })
        );
        return;
      }

      store.dispatch(
        authActions.profileLoadFailed({
          message: error instanceof Error ? error.message : "Failed to restore session.",
        })
      );
      await this.clearSession();
    }
  }

  /**
   * POST /auth/v1/token → store tokens → GET /me.
   * Accepts typed LoginRequest; maps to wire `{ emailOrMobile, password }` in one place only.
   */
  async login(credentials: LoginRequest): Promise<void> {
    store.dispatch(authActions.loginStarted());

    try {
      const wire = toLoginWireDto(credentials);
      const response = await this.api.post(LOGIN_ENDPOINT, wire, {
        skipErrorToast: true,
      });
      const session = parseLoginResponse(response.data);
      this.establishSession(session);
      await this.loadProfile();
    } catch (error) {
      await this.handleLoginFailure(error);
      throw error;
    }
  }

  /** POST /users/v1/register-user → tokens → GET /me. */
  async register(payload: RegisterRequestDto): Promise<void> {
    store.dispatch(authActions.loginStarted());

    try {
      const response = await this.api.post(REGISTER_ENDPOINT, payload, {
        skipErrorToast: true,
      });
      const session = parseLoginResponse(response.data);
      this.establishSession(session);
      await this.loadProfile();
    } catch (error) {
      await this.handleLoginFailure(error);
      throw error;
    }
  }

  /**
   * GET /api/auth/v1/{provider}/init?redirectUri=… → authUrl.
   * `redirectUri` is the SPA complete page the API redirects to after tokens are issued.
   */
  async getOAuthAuthUrl(provider: OAuthProvider, redirectUri: string): Promise<string> {
    const response = await this.api.get(oauthInitApiPath(provider), {
      params: { redirectUri },
      headers: { Accept: "application/json" },
      skipErrorToast: true,
    });

    const authUrl = extractOAuthAuthUrl(response?.data);
    if (!authUrl) {
      const label = oauthProviderDisplayName(provider);
      throw new AuthSessionError(`${label} sign-in is not available right now.`);
    }

    return authUrl;
  }

  /** GET /api/auth/v1/google/init?redirectUri=… → authUrl. */
  async getGoogleAuthUrl(redirectUri: string): Promise<string> {
    return this.getOAuthAuthUrl("google", redirectUri);
  }

  /** GET /api/auth/v1/facebook/init?redirectUri=… → authUrl. */
  async getFacebookAuthUrl(redirectUri: string): Promise<string> {
    return this.getOAuthAuthUrl("facebook", redirectUri);
  }

  /** GET /api/auth/v1/google/callback?code&state → tokens → GET /me. */
  async loginWithGoogleCallback(code: string, state: string): Promise<void> {
    store.dispatch(authActions.loginStarted());

    try {
      const response = await this.api.get(GOOGLE_CALLBACK_ENDPOINT, {
        params: { code, state },
        headers: { Accept: "application/json" },
        skipErrorToast: true,
      });
      const session = parseLoginResponse(response.data);
      this.establishSession(session);
      await this.loadProfile();
    } catch (error) {
      await this.handleLoginFailure(error);
      throw error;
    }
  }

  /**
   * Browser return from API OAuth redirect: `/oauth/{provider}/complete?token=…&refreshToken=…`.
   * `token` is required; `refreshToken` is optional.
   */
  async loginWithOAuthRedirectTokens(
    accessToken: string,
    refreshToken?: string | null
  ): Promise<void> {
    const token = typeof accessToken === "string" ? accessToken.trim() : "";
    if (!token) {
      throw new AuthSessionError("Sign-in response is incomplete.");
    }

    store.dispatch(authActions.loginStarted());

    try {
      const refresh =
        typeof refreshToken === "string" && refreshToken.trim().length > 0
          ? refreshToken.trim()
          : null;

      this.storage.setTokens({
        accessToken: token,
        refreshToken: refresh,
      });
      this.lastKnownTokenVersion = null;
      store.dispatch(
        authActions.sessionEstablished({
          requiresRestaurantSelection: false,
          loginTicket: null,
          memberships: [],
        })
      );
      await this.loadProfile();
    } catch (error) {
      await this.handleLoginFailure(error);
      throw error;
    }
  }

  /** @deprecated Prefer loginWithOAuthRedirectTokens */
  async loginWithGoogleRedirectTokens(
    accessToken: string,
    refreshToken?: string | null
  ): Promise<void> {
    return this.loginWithOAuthRedirectTokens(accessToken, refreshToken);
  }

  /** POST /auth/v1/password/forgot. */
  async requestPasswordReset(payload: ForgotPasswordRequestDto): Promise<ForgotPasswordResponseDto> {
    const response = await this.api.post<ForgotPasswordResponseDto>(FORGOT_PASSWORD_ENDPOINT, {
      emailOrMobile: payload.emailOrMobile,
    }, {
      skipErrorToast: true,
    });
    assertAuthActionSucceeded(response.data, "Unable to send reset OTP.");
    return response.data ?? {};
  }

  /** POST /auth/v1/password/reset. */
  async resetPassword(payload: ResetPasswordRequestDto): Promise<void> {
    const response = await this.api.post(RESET_PASSWORD_ENDPOINT, {
      emailOrMobile: payload.emailOrMobile,
      requestId: payload.requestId,
      otp: payload.otp,
      newPassword: payload.newPassword,
    }, {
      skipErrorToast: true,
    });
    assertAuthActionSucceeded(response.data, "Unable to reset password.");
  }

  private async handleLoginFailure(error: unknown): Promise<void> {
    await this.clearSession();

    if (isNetworkError(error)) {
      store.dispatch(authActions.setUnauthenticated());
      return;
    }

    if (axios.isAxiosError(error) && error.response?.status === 401) {
      store.dispatch(authActions.setUnauthenticated());
      throw new AuthCredentialsError();
    }

    store.dispatch(authActions.setUnauthenticated());
  }

  /** GET /auth/v1/me — single source of truth. Deduped inflight. */
  async fetchMe(): Promise<AuthenticatedUser> {
    if (this.inflightProfile) return this.inflightProfile;

    this.inflightProfile = this.requestMe().finally(() => {
      this.inflightProfile = null;
    });

    return this.inflightProfile;
  }

  private async requestMe(): Promise<AuthenticatedUser> {
    const response = await this.api.get<MeApiResponseDto>(ME_ENDPOINT, {
      headers: { Accept: "application/json" },
    });
    assertMeSuccess(response.data);
    const identity = mapAuthenticatedUserDto(response.data.data);
    this.assertTokenVersion(identity);
    return identity;
  }

  /** Load profile into Redux from /me. */
  async loadProfile(options: { preserveEnrichment?: boolean } = {}): Promise<AppUser> {
    store.dispatch(authActions.profileLoadStarted());

    try {
      const identity = await this.fetchMe();
      const currentUser = store.getState().auth.user;
      const enrichment =
        options.preserveEnrichment && currentUser
          ? currentUser.enrichment
          : undefined;

      const appUser = toAppUser(identity, enrichment);

      if (
        currentUser &&
        identityEquals(currentUser.identity, identity) &&
        options.preserveEnrichment
      ) {
        store.dispatch(authActions.refreshSucceeded());
        return currentUser;
      }

      store.dispatch(authActions.profileLoaded(appUser));
      return appUser;
    } catch (error) {
      if (error instanceof TokenVersionMismatchError) {
        await this.clearSession();
        store.dispatch(authActions.setUnauthenticated());
        throw error;
      }

      if (error instanceof AuthProfileError) {
        store.dispatch(authActions.profileLoadFailed({ message: error.message }));
        throw error;
      }

      if (isNetworkError(error)) {
        store.dispatch(
          authActions.profileLoadFailed({ message: new AuthNetworkError().message })
        );
        throw new AuthNetworkError();
      }

      const message = error instanceof Error ? error.message : "Failed to load profile.";
      store.dispatch(authActions.profileLoadFailed({ message }));
      throw error;
    }
  }

  /** POST /auth/v1/refresh — single-flight. */
  async refreshTokens(): Promise<TokenPair> {
    if (this.inflightRefresh) return this.inflightRefresh;

    const refreshToken = this.storage.getRefreshToken();
    if (!refreshToken) {
      throw new AuthSessionError("No refresh token available.");
    }

    store.dispatch(authActions.refreshStarted());

    this.inflightRefresh = this.requestRefresh(refreshToken)
      .then((pair) => {
        this.storage.setTokens(pair);
        store.dispatch(authActions.refreshSucceeded());
        return pair;
      })
      .catch(async (error) => {
        await this.clearSession();
        store.dispatch(authActions.setUnauthenticated());
        throw error;
      })
      .finally(() => {
        this.inflightRefresh = null;
      });

    return this.inflightRefresh;
  }

  private async requestRefresh(refreshToken: string): Promise<TokenPair> {
    try {
      const response = await this.api.post<RefreshTokenResponseDto>(REFRESH_ENDPOINT, {
        refreshToken,
      });
      const data = response.data;
      if (!data?.accessToken || !data?.refreshToken) {
        throw new AuthSessionError("Invalid refresh response.");
      }
      return { accessToken: data.accessToken, refreshToken: data.refreshToken };
    } catch (error) {
      if (isNetworkError(error)) throw new AuthNetworkError();
      if (isUnauthorized(error)) throw new AuthSessionError();
      throw error;
    }
  }

  /** Clear session locally and reset dependent client state. */
  async logout(): Promise<void> {
    const accessToken = this.storage.getAccessToken();
    const refreshToken = this.storage.getRefreshToken();

    if (accessToken) {
      // Drop the customer↔browser push association while the token is still valid. Only the
      // RasoiX row is deactivated — the browser's own push subscription is left alone, so a
      // later sign-in on this device can opt in again. Best-effort; never blocks sign-out.
      // Not done in invalidateSession(): that path runs after a 401, where this would 401 too.
      await revokeOrderNotificationsForSession();

      try {
        await this.api.post(
          LOGOUT_ENDPOINT,
          { refreshToken },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } catch {
        // Best-effort server revocation — local clear always proceeds
      }
    }

    await this.clearSession();
    store.dispatch(authActions.setUnauthenticated());
    this.clearDependentState();
  }

  /** Silent logout without navigation — used by interceptor. */
  async invalidateSession(): Promise<void> {
    await this.clearSession();
    store.dispatch(authActions.setUnauthenticated());
    this.clearDependentState();
  }

  getAccessToken(): string | null {
    return this.storage.getAccessToken();
  }

  isRefreshInFlight(): boolean {
    return this.inflightRefresh !== null;
  }

  private establishSession(session: LoginSession): void {
    this.storage.setTokens({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
    });
    this.lastKnownTokenVersion = null;
    store.dispatch(
      authActions.sessionEstablished({
        requiresRestaurantSelection: session.requiresRestaurantSelection,
        loginTicket: session.loginTicket,
        memberships: session.memberships,
      })
    );
  }

  private assertTokenVersion(identity: AuthenticatedUser): void {
    if (
      this.lastKnownTokenVersion !== null &&
      this.lastKnownTokenVersion !== identity.tokenVersion
    ) {
      throw new TokenVersionMismatchError();
    }
    this.lastKnownTokenVersion = identity.tokenVersion;
  }

  private async clearSession(): Promise<void> {
    this.storage.clearTokens();
    this.lastKnownTokenVersion = null;
    this.inflightProfile = null;
    // ADR Phase 1: any token removal expires prior-session Preview transport memo
    // (covers bootstrap/refresh/token-version paths that skip clearDependentState).
    invalidateQueryPrefix(QUERY_KEY_PREFIX.pricingPreview);
  }

  private clearDependentState(): void {
    clearAuthDependentClientState();
  }
}

export const authService = new AuthService();

/** Test-only factory. */
export function createAuthService(api: AxiosInstance, storage: TokenStorage): AuthService {
  return new AuthService(api, storage);
}
